import { Route, Routes, useLocation } from "react-router-dom";
import Header from "./components/Header";
import "./styles/App.css";
import Library from "./components/pages/Library";
import Constructor from "./components/pages/Constructor";
import Profile from "./components/pages/Profile";
import AnimatedCursor from "react-animated-cursor";
import Footer from "./components/Footer";
import Schedule from "./components/pages/Schedule";
import Shop from "./components/pages/Shop";
import About from "./components/pages/About";
import { useEffect, useState } from "react";
import ChatbotModal from "./components/utilPages/Chatbot";
import { ChatbotTrans, ToasterTrans } from "./utils/translations";
import { useAppContext } from "./context/AppContext";
import RequireAuthRoute from "./utils/RequireAuthRoute";
import CourseGate from "./components/utilPages/CourseGate";
import Callback from "./components/pages/Callback";
import { useAuth0 } from "@auth0/auth0-react";
import { getUser, updateUser } from "./services/userService";
import {
  applyPendingReferralClaim,
  PENDING_REFERRAL_STORAGE_KEY,
} from "./utils/pendingReferralClaim";
import Toaster from "./components/utilPages/Toaster";
import SavedPractices from "./components/pages/SavedPractices";
import Offline from "./components/pages/Offline";
import ReactGA from "react-ga4";
import Blog from "./components/pages/Blog";
import BlogPostPage from "./components/pages/BlogPostPage";
import { initFacebookPixel, trackPixelEvent } from "./utils/pixel";
import LandingPage from "./components/pages/Landing";
import WomensDayGift from "./components/pages/WomensDayGift";
import OnlineClubPage from "./components/pages/OnlineClub";
import Loader from "./components/utilPages/Loader";
import SuccessfulPayment from "./components/pages/SuccessOnlineClub";
import SuccessPage from "./components/pages/SuccessPage";
import Subscribe from "./components/pages/Subscribe";
import UnsubscribeSuccess from "./components/pages/UnsubscribeSuccess";
import MembersOnly from "./components/pages/MemberOnly";
import AdminDashboard from './components/pages/AdminDashboard';
import NotFound from "./components/pages/NotFound";
import IndividualClasses from "./components/pages/IndividualClasses";
import SocialHub from "./components/pages/SocialHub";
import Courses from "./components/pages/Courses/Courses";
import CourseDetail from "./components/pages/Courses/CourseDetail";
import CourseClassDetail from "./components/pages/Courses/CourseClassDetail";
import SanskritYogaPage from "./components/pages/SanskritYogaPage";
import AsanaCourseLanding from "./components/pages/AsanaCourseLanding";
import LibraryFolderPage from "./components/pages/LibraryFolderPage";
import LibraryCoursesPage from "./components/pages/LibraryCoursesPage";
import LibraryLivesPage from "./components/pages/LibraryLivesPage";

const TRACKING_ID = "G-QKNDY186TQ";
ReactGA.initialize(TRACKING_ID);

const usePageTracking = () => {
  const location = useLocation();

  useEffect(() => {
    ReactGA.send({ hitType: "pageview", page: location.pathname });
  }, [location]);
};

function App() {
  const location = useLocation();
  const [isChatOpen, setIsChatOpen] = useState(false);
  const { language, setUser, user, token, setToken, toaster, setToaster, loading, setLoading, premiumRoles } = useAppContext();
  const {
    isAuthenticated,
    user: auth0user,
    getAccessTokenSilently,
    isLoading: authLoading,
    loginWithRedirect,
  } = useAuth0();
  const [showCursor, setShowCursor] = useState(true);
  usePageTracking();

  //todo more logging
  // const handleButtonClick = () => {
  //   ReactGA.event({
  //     category: "User",
  //     action: "Clicked Sign Up Button",
  //     label: "Homepage Signup"
  //   });
  // };
  
  // <button onClick={handleButtonClick}>Sign Up</button>
  
  useEffect(() => {
    if (user) {
      console.log(user)
    }
  }, [user])

  useEffect(() => {
    initFacebookPixel();
    // Подключение Pixel если его еще нет
    if (!window.fbq) return;
  
    // Скролл до 90%
    const handleScroll = () => {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPercent = (scrollTop / docHeight) * 100;
  
      if (scrollPercent >= 90 && !window.__scrollTracked) {
        window.fbq('trackCustom', 'Scrolled90');
        window.__scrollTracked = true;
      }
    };
  
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get("ref");
    if (ref) {
      const trimmed = ref.trim();
      if (trimmed && trimmed.length <= 512) {
        localStorage.setItem(PENDING_REFERRAL_STORAGE_KEY, trimmed);
      }
    }
  }, [location.search]);

  // Referral link while logged out → Auth0 login, then return to same path (ref stays in URL; pendingReferral also stored above)
  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) return;
    if (location.pathname === "/callback") return;

    const params = new URLSearchParams(location.search);
    const ref = params.get("ref")?.trim();
    if (!ref || ref.length > 512) return;

    const returnTo = `${location.pathname}${location.search}`;
    localStorage.setItem("redirectTo", returnTo);

    loginWithRedirect({
      ui_locales: language === "russian" ? "ru" : "en",
    });
  }, [
    authLoading,
    isAuthenticated,
    location.pathname,
    location.search,
    language,
    loginWithRedirect,
  ]);

  useEffect(() => {
    // 1) Wait until Auth0 is done initializing
    if (authLoading) return;

    // 2) If we’re authenticated but haven’t fetched our app-user yet → fetch them
    if (isAuthenticated && !user) {
      ;(async () => {
        try {
          const token = await getAccessTokenSilently({
            audience: "https://onceuponamat.com/api/v2/"
          });
          const pendingBefore = localStorage.getItem(PENDING_REFERRAL_STORAGE_KEY)?.trim();
          let appUser = await getUser(token);
          const hadReferredByBefore = !!appUser?.referredBy;
          appUser = await applyPendingReferralClaim(token, appUser);
          if (pendingBefore && appUser?.referredBy && !hadReferredByBefore) {
            setToaster({
              message: ToasterTrans[language].referralSaved,
              type: "success",
              show: true,
            });
          }
          if (appUser && appUser.language !== language) {
            try {
              appUser = await updateUser(token, { language });
            } catch (e) {
              console.error("Error syncing language preference:", e);
            }
          }
          setToken(token);
          setUser(appUser);
          trackPixelEvent("Login")
        } catch (e) {
          console.error(e);
        } finally {
          setLoading(false);
        }
      })();
    } else {
      // either not authenticated, or we already have `user` → we’re done
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, auth0user, user, language, setToaster]);

  useEffect(() => {
    if (authLoading || !isAuthenticated || !user || !token) return;
    (async () => {
      const pendingBefore = localStorage.getItem(PENDING_REFERRAL_STORAGE_KEY)?.trim();
      const hadReferredByBefore = !!user?.referredBy;
      const next = await applyPendingReferralClaim(token, user);
      if (next !== user) setUser(next);
      if (pendingBefore && next?.referredBy && !hadReferredByBefore) {
        setToaster({
          message: ToasterTrans[language].referralSaved,
          type: "success",
          show: true,
        });
      }
    })();
  }, [authLoading, isAuthenticated, user?._id, user?.referredBy, token, setUser, setToaster, language, location.search]);

  useEffect(() => {
    const checkScreen = () => setShowCursor(window.innerWidth > 768);
    checkScreen();
    window.addEventListener("resize", checkScreen);
    return () => window.removeEventListener("resize", checkScreen);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname]);

  return (
    <div className="App flex flex-col min-h-screen">
      {showCursor && (
        <AnimatedCursor
          innerSize={8}
          outerSize={20}
          color="198, 188, 180"
          outerAlpha={0.2}
          innerScale={0.000001}
          outerScale={4}
          showSystemCursor={true}
          clickables={[
            "a",
            'input[type="text"]',
            'input[type="email"]',
            'input[type="number"]',
            'input[type="submit"]',
            'input[type="image"]',
            "label[for]",
            "select",
            "textarea",
            "button",
            ".link",
          ]}
        />
      )}
      {(authLoading || loading) && location.pathname !== "/links" && (
        <Loader load={authLoading || loading} />
      )}
      {!location.pathname.startsWith("/8march") && location.pathname !== "/success" && location.pathname !== "/landing" && location.pathname !== "/individual" && location.pathname !== "/links" && <Header />}
      <div className="flex-1 flex flex-col">
      <Routes>
        <Route path="/library" element={<Library />} />
        <Route path="/library/courses" element={<LibraryCoursesPage />} />
        <Route path="/library/lives" element={<RequireAuthRoute requiredRoles={['onlineClub', 'admin']}><LibraryLivesPage /></RequireAuthRoute>} />
        <Route path="/library/:slug" element={<LibraryFolderPage />} />
        <Route path="/links" element={<SocialHub />} />
        <Route path="/landing" element={<LandingPage />} />
        <Route path="/8march" element={<WomensDayGift />} />
        <Route path="/schedule" element={<Schedule />} />
        <Route path="/individual" element={<IndividualClasses />} />
        {/* <Route path="/offline" element={<Offline />} /> */}
        <Route path="/constructor" element={<RequireAuthRoute requiredRoles={premiumRoles}><Constructor /></RequireAuthRoute>} />
        <Route path="/constructor-trial" element={<Constructor isTrial />} />
        <Route path="/shop" element={<Shop />} />
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:id" element={<BlogPostPage />} />
        <Route path="/yoga-asanas" element={<AsanaCourseLanding />} />
        <Route path="/course/yoga-asanas" element={<RequireAuthRoute><CourseGate courseSlug="sanskrit-asanas"><SanskritYogaPage /></CourseGate></RequireAuthRoute>} />
        <Route path="/courses" element={<Courses />} />
        <Route path="/courses/:courseSlug" element={<CourseDetail />} />
        <Route path="/courses/:courseSlug/classes/:classSlug" element={<CourseClassDetail />} />
        <Route path="/profile" element={<RequireAuthRoute><Profile /></RequireAuthRoute>} />
        <Route path="/about" element={<About />} />
        <Route path="/saved" element={<SavedPractices />} />
        <Route path="/onlineClub" element={<OnlineClubPage />} />
        <Route path="/successfulPayment" element={<SuccessfulPayment />} />
        <Route path="/success" element={<SuccessPage />} />
        <Route path="/subscribe" element={<Subscribe />} />
        <Route path="/unsubscribeSuccess" element={<UnsubscribeSuccess />} />
        <Route path="/membersOnly" element={<MembersOnly />} />
        <Route path="/admin" element={<RequireAuthRoute requiredRoles={['admin']}><AdminDashboard /></RequireAuthRoute>} />
        <Route path="/not-found" element={<NotFound />} />
        <Route path="/" element={<Schedule />} />
        <Route path="/callback" element={<Callback />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </div>
      {/* //todo uncomment and implement chat */}
      {/* <button
        onClick={() => setIsChatOpen(true)}
        className="fixed bg-beige hover:bg-brown text-white p-3 rounded-full shadow-lg z-50"
        style={{ bottom: '20px', right: '20px' }}
      >
        {ChatbotTrans[language].chatbot}
      </button> */}
      {/* <ChatbotModal isOpen={isChatOpen} onClose={() => setIsChatOpen(false)} /> */}
      {toaster.show && (
        <Toaster
          message={toaster.message}
          type={toaster.type}
          onClose={() => setToaster({ ...toaster, show: false })}
        />
      )}
      {/* <PWAInstallBanner /> */}
      {!(authLoading || loading) && location.pathname !== "/links" && <Footer />}
    </div>
  );
}

export default App;
